/**
 * Help Widget Context
 *
 * Manages state for the context-aware help system.
 * Tracks current context and panel visibility.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { DocumentationDoc, DocumentationByCategory, ToolId } from '../../types/documentation.types';
import { DocumentationService } from '../../services/DocumentationService';

interface HelpContextValue {
  /** Current context trigger (e.g., "episode-composer:draft") */
  currentContext: string | null;

  /** Set the current context for context-aware help */
  setContext: (context: string | null) => void;

  /** Whether the help panel is open */
  isOpen: boolean;

  /** Open the help panel */
  openPanel: () => void;

  /** Close the help panel */
  closePanel: () => void;

  /** Toggle the help panel */
  togglePanel: () => void;

  /** Current view mode */
  mode: 'context' | 'browse';

  /** Set the view mode */
  setMode: (mode: 'context' | 'browse') => void;

  /** Context-aware docs for current context */
  contextDocs: DocumentationDoc[];

  /** Loading state for docs */
  isLoading: boolean;

  /** Currently selected doc for detail view */
  selectedDoc: DocumentationDoc | null;

  /** Set selected doc */
  setSelectedDoc: (doc: DocumentationDoc | null) => void;

  /** All docs grouped by category (for browse mode) */
  browseDocs: DocumentationByCategory[];

  /** Current tool ID */
  toolId: ToolId;

  /** Set the tool ID */
  setToolId: (toolId: ToolId) => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

interface HelpProviderProps {
  children: React.ReactNode;
  /** Default tool ID */
  defaultToolId?: ToolId;
  /** Initial context (optional) */
  initialContext?: string | null;
}

export function HelpProvider({
  children,
  defaultToolId = 'episode-composer',
  initialContext = null,
}: HelpProviderProps) {
  const [currentContext, setCurrentContext] = useState<string | null>(initialContext);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'context' | 'browse'>('context');
  const [contextDocs, setContextDocs] = useState<DocumentationDoc[]>([]);
  const [browseDocs, setBrowseDocs] = useState<DocumentationByCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentationDoc | null>(null);
  const [toolId, setToolId] = useState<ToolId>(defaultToolId);

  // Fetch context-aware docs when context changes
  useEffect(() => {
    if (!currentContext) {
      setContextDocs([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    DocumentationService.getByContext(currentContext)
      .then((docs) => {
        if (!cancelled) {
          setContextDocs(docs);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error('[HelpContext] Error fetching context docs:', error);
        if (!cancelled) {
          setContextDocs([]);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentContext]);

  // Fetch browse docs when panel opens in browse mode or mode changes to browse
  useEffect(() => {
    if (!isOpen || mode !== 'browse') {
      return;
    }

    // Only fetch if we don't have browse docs yet
    if (browseDocs.length > 0) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    DocumentationService.getAllGroupedByCategory(toolId)
      .then((grouped) => {
        if (!cancelled) {
          setBrowseDocs(grouped);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error('[HelpContext] Error fetching browse docs:', error);
        if (!cancelled) {
          setBrowseDocs([]);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, toolId, browseDocs.length]);

  // Clear browse docs when tool changes
  useEffect(() => {
    setBrowseDocs([]);
  }, [toolId]);

  const setContext = useCallback((context: string | null) => {
    setCurrentContext(context);
    // Clear selected doc when context changes
    setSelectedDoc(null);
  }, []);

  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    // Clear selected doc when closing
    setSelectedDoc(null);
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => !prev);
    if (isOpen) {
      setSelectedDoc(null);
    }
  }, [isOpen]);

  const value = useMemo<HelpContextValue>(() => ({
    currentContext,
    setContext,
    isOpen,
    openPanel,
    closePanel,
    togglePanel,
    mode,
    setMode,
    contextDocs,
    isLoading,
    selectedDoc,
    setSelectedDoc,
    browseDocs,
    toolId,
    setToolId,
  }), [
    currentContext, setContext, isOpen, openPanel, closePanel, togglePanel,
    mode, setMode, contextDocs, isLoading, selectedDoc, setSelectedDoc,
    browseDocs, toolId, setToolId,
  ]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelpContext(): HelpContextValue {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelpContext must be used within a HelpProvider');
  }
  return context;
}

/**
 * Hook for setting help context based on episode status.
 * Automatically updates the help context when status changes.
 */
export function useHelpContextForStatus(status: string | null | undefined): void {
  const { setContext } = useHelpContext();

  useEffect(() => {
    if (status) {
      const context = DocumentationService.buildEpisodeStatusTrigger(status);
      setContext(context);
    } else {
      setContext(null);
    }

    return () => {
      setContext(null);
    };
  }, [status, setContext]);
}
